import React, { useEffect, useRef } from "react";

import { css, cx } from "@emotion/css";

import { FontSizes } from "@/assets/fonts.ts";
import { useChatContent } from "@/hooks/useChatContent.ts";
import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useComponentScroll } from "@/hooks/useScroll.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CloseCardButton } from "@/uiKit/atoms/CloseCardButton.tsx";
import { useChatDrawer } from "@/uiKit/atoms/page/PageDrawerState.ts";
import { MessagesList } from "@/uiKit/molecules/chat/MessagesList.tsx";
import { SendMessageForm } from "@/uiKit/molecules/chat/SendMessageForm.tsx";

export type ChatMessageCardProps = {
  className?: string;
};

export const ChatMessageCard: React.FC<ChatMessageCardProps> = ({ className }) => {
  const drawer = useChatDrawer();
  const styles = useStyles(makeStyles);
  const patientId = usePatientIdFromURL();
  const me = Queries.user.useMe({});
  const messages = useChatContent(patientId, me.id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { scrollPosition, onScroll } = useComponentScroll({ reversed: true });

  useEffect(() => {
    if (scrollPosition.isNearBottom) {
      messages.loadMore();
    }
  }, [scrollPosition.isNearBottom, messages]);

  return (
    <Card id="messaging-panel" className={cx(styles.container, className)}>
      <CloseCardButton onClick={drawer.close} />
      <CardContent ref={scrollRef} className={cx(styles.messages, "scrollbar")} onScroll={onScroll}>
        <MessagesList patientId={patientId} />
      </CardContent>
      <CardFooter className={styles.actions}>
        <SendMessageForm
          className={styles.sendMessage}
          onSendMessage={() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </CardFooter>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
  }),
  header: css({
    ".MuiCardHeader-action": {
      alignSelf: "center",
    },
  }),
  messages: css({
    padding: theme.spacing(8, 4, 0, 8),
    width: "100%",
    flex: "1 1",
    display: "flex",
    overflowY: "auto",
    flexDirection: "column-reverse",
  }),
  icons: css({
    width: FontSizes.body,
    height: FontSizes.body,
    cursor: "pointer",
  }),
  actions: css({
    padding: 0,
  }),
  sendMessage: css({
    flex: "1 1",
  }),
});
